import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import { useChangeWeightUnitMutation } from '../../../../store/api-slice';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';
import { useSnackbar } from 'notistack';

interface WeightUnit {
  id: string;
  label: string;
}

const availableWeightUnits: WeightUnit[] = [
  {
    id: 'kg',
    label: 'Kilogram',
  },
  {
    id: 'lb',
    label: 'Pound',
  },
  {
    id: 'st',
    label: 'Stone',
  },
];

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;
  onClose?(): void;
  onCreated?(): void;
}

const ChangeWeightUnitDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen, onClose, onCreated }) => {
    const config = useConfig();
    const { authUser } = useAuthUser();
    const [changeWeightUnit, { isLoading }] = useChangeWeightUnitMutation();
    const [weightUnit, setWeightUnit] = useState('');
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
      }
    }, [onCreated]);

    useEffect(() => {
      if (isOpen && devices.length > 0) {
        setWeightUnit(devices[0].weightUnit || availableWeightUnits[0].id);
      }
    }, [devices, isOpen]);

    return (
      <>
        <Dialog
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Change Weight Unit</DialogTitle>
          <DialogContent>
            <MedSelect
              name="weight-unit-1"
              value={weightUnit}
              options={availableWeightUnits}
              placeholder="Weight Unit"
              onChange={(e) => {
                setWeightUnit(e.target.value as string);
              }}
              valueGetter={(wu) => wu.id}
              optionTextGetter={(wu) => wu.label}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              onClick={async () => {
                const resp: any = await changeWeightUnit({
                  weightUnit,
                  apiBaseURL: config.apiBaseURL,
                  orgIDs: authUser.selectedOrgs,
                  devices,
                });
                if (!resp.error) {
                  handleClose();
                  handleCreated();
                  return;
                }
                enqueueSnackbar(parseErrorMessage(resp.error), {
                  variant: 'error',
                });
              }}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default ChangeWeightUnitDialog;
export { availableWeightUnits as WeightUnits };
